import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, Building2, CheckCircle2, KeyRound, Loader2, Mail, RotateCw, Send, Settings, Users, Palette } from 'lucide-react';
import { OidcSettingsForm } from './OidcSettings';
import type { AdminSettings, AdminUser, MailLogEntry, SmtpSecurity } from '@kaba/shared';
import { api } from '../lib/api';
import { useMe } from '../lib/auth';
import { navigate, setQuery, useLocation } from '../lib/router';
import { toast } from '../lib/toast';
import { PasswordInput } from './ui/PasswordInput';
import { Button, cx, IconButton } from './ui/ui';

export const inputCls =
  'min-h-11 w-full rounded-lg border border-[var(--line)] bg-[var(--panel)] px-3 text-base outline-none focus:border-accent sm:min-h-10 sm:text-sm disabled:opacity-60';

export function FormField({ label, hint, children, htmlFor }: { label: string; hint?: React.ReactNode; children: React.ReactNode; htmlFor?: string }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={htmlFor} className="text-sm font-medium">
        {label}
      </label>
      {children}
      {hint && <p className="text-xs text-[var(--ink-soft)]">{hint}</p>}
    </div>
  );
}

/** 0.84.1: Reihenfolge nach Häufigkeit – Laufendes zuerst, Einmaliges (Anmeldung, E-Mail) hinten */
const TABS = [
  ['general', 'Allgemein', Settings],
  ['users', 'Personen', Users],
  ['teams', 'Teams', Building2],
  ['appearance', 'Erscheinungsbild', Palette],
  ['login', 'Anmeldung', KeyRound],
  ['mail', 'E-Mail', Mail],
] as const;
type Tab = (typeof TABS)[number][0];

import { BrandingSummary } from './branding/BrandingSummary';

export function AdminPage() {
  const { data: me } = useMe();
  const { query } = useLocation();
  const tab = (query.get('tab') as Tab) || 'general';
  const { data, isLoading } = useQuery({
    queryKey: ['admin-settings'],
    queryFn: () => api.get<AdminSettings>('/api/admin/settings'),
    enabled: !!me?.isAdmin,
  });

  if (me && !me.isAdmin)
    return (
      <div className="p-8">
        <p className="mb-3">Dieser Bereich ist nur für Administratoren.</p>
        <Button onClick={() => navigate('/')}>Zur Übersicht</Button>
      </div>
    );

  return (
    <div className="mx-auto flex min-h-full max-w-3xl flex-col px-4 pb-16">
      <header className="pt-safe flex items-center gap-2 py-3">
        <IconButton label="Zur Übersicht" onClick={() => navigate('/')}>
          <ArrowLeft size={20} />
        </IconButton>
        <h1 className="flex-1 text-xl font-bold">Administration</h1>
        {data && <span className="text-xs text-[var(--ink-soft)]">Version {data.version}</span>}
      </header>
      <main>
      {/* Linie als Schatten statt Rahmen: kein -mb-px-Überstand, darum kein senkrechter Scrollbalken; waagerecht wischen am Handy ohne sichtbaren Balken */}
      <div
        role="tablist"
        aria-label="Bereiche"
        className="mb-5 flex gap-1 overflow-x-auto overflow-y-hidden shadow-[inset_0_-1px_0_var(--line)] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {TABS.map(([k, label, Icon]) => (
          <button
            key={k}
            role="tab"
            aria-selected={tab === k}
            onClick={() => setQuery({ tab: k }, { replace: true })}
            className={cx(
              'inline-flex min-h-11 shrink-0 items-center gap-1.5 border-b-2 px-3 text-sm font-medium whitespace-nowrap',
              tab === k ? 'border-accent' : 'border-transparent text-[var(--ink-soft)]',
            )}
          >
            <Icon size={16} /> {label}
          </button>
        ))}
      </div>
        {isLoading || !data ? (
          <Loader2 className="animate-spin text-[var(--ink-soft)]" />
        ) : tab === 'general' ? (
          <GeneralSettingsForm settings={data} />
        ) : tab === 'mail' ? (
          <MailSettings settings={data} />
        ) : tab === 'appearance' ? (
          <BrandingSummary target={{ scope: 'instance' }} />
        ) : tab === 'login' ? (
          <OidcSettingsForm settings={data} />
        ) : tab === 'teams' ? (
          <TeamsMigration />
        ) : (
          <UsersList />
        )}
      </main>
    </div>
  );
}

/* ---------- E-Mail ---------- */

const PRESETS: { label: string; host: string; port: number; security: SmtpSecurity }[] = [
  { label: 'IONOS', host: 'smtp.ionos.de', port: 587, security: 'starttls' },
  { label: 'Strato', host: 'smtp.strato.de', port: 465, security: 'tls' },
  { label: 'Hetzner', host: 'mail.your-server.de', port: 587, security: 'starttls' },
  { label: 'Microsoft 365', host: 'smtp.office365.com', port: 587, security: 'starttls' },
  { label: 'Gmail / Google Workspace', host: 'smtp.gmail.com', port: 587, security: 'starttls' },
  { label: 'Mailjet', host: 'in-v3.mailjet.com', port: 587, security: 'starttls' },
  { label: 'Brevo', host: 'smtp-relay.brevo.com', port: 587, security: 'starttls' },
];

const SECURITY_LABEL: Record<SmtpSecurity, string> = {
  starttls: 'STARTTLS (meist Port 587)',
  tls: 'SSL/TLS (meist Port 465)',
  none: 'Keine (nur im internen Netz)',
};
const DEFAULT_PORT: Record<SmtpSecurity, number> = { starttls: 587, tls: 465, none: 25 };

function MailSettings({ settings }: { settings: AdminSettings }) {
  const qc = useQueryClient();
  const { data: me } = useMe();
  const s = settings.smtp;
  const [form, setForm] = useState({
    enabled: s.source === 'none' ? true : s.enabled,
    host: s.host,
    port: s.port,
    security: s.security,
    username: s.username,
    fromEmail: s.fromEmail,
    fromName: s.fromName,
    replyTo: s.replyTo,
    allowSelfSigned: s.allowSelfSigned,
  });
  /** undefined = unverändert, null = löschen, string = neu */
  const [password, setPassword] = useState<string | null | undefined>(undefined);
  const [testTo, setTestTo] = useState(me?.email ?? '');
  const [busy, setBusy] = useState<'save' | 'test' | null>(null);
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (me?.email && !testTo) setTestTo(me.email);
  }, [me?.email, testTo]);

  const payload = () => ({ ...form, port: Number(form.port), ...(password !== undefined ? { password } : {}) });

  const save = async () => {
    setBusy('save');
    setResult(null);
    try {
      const res = await api.put<AdminSettings>('/api/admin/settings/smtp', payload());
      qc.setQueryData(['admin-settings'], res);
      void qc.invalidateQueries({ queryKey: ['config'] });
      setPassword(undefined);
      toast.info('E-Mail-Einstellungen gespeichert.');
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const test = async () => {
    setBusy('test');
    setResult(null);
    try {
      setResult(await api.post<{ ok: boolean; message: string }>('/api/admin/settings/smtp/test', { ...payload(), to: testTo }));
    } catch (err) {
      setResult({ ok: false, message: (err as Error).message });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-1">
        <h2 className="text-lg font-semibold">E-Mail-Versand (SMTP)</h2>
        <p className="text-sm text-[var(--ink-soft)]">
          Wird für Einladungen, „Passwort vergessen“, Erwähnungen, Zuweisungen und Fristerinnerungen gebraucht. Ohne SMTP
          funktioniert KaBa weiter – Benachrichtigungen gibt es dann nur in der App.
        </p>
        {s.source === 'env' && (
          <p className="mt-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-950 dark:text-amber-100">
            Die aktuellen Werte kommen aus Umgebungsvariablen (SMTP_…). Wenn du hier speicherst, gelten ab dann die Werte aus diesem
            Formular.
          </p>
        )}
        {s.passwordUnreadable && (
          <p className="mt-2 flex gap-2 rounded-lg bg-rose-50 p-3 text-sm text-rose-800 dark:bg-rose-950 dark:text-rose-100">
            <AlertTriangle size={18} className="shrink-0" />
            Das gespeicherte Passwort lässt sich nicht mehr entschlüsseln (KABA_SECRET wurde geändert). Bitte neu eingeben.
          </p>
        )}
      </section>

      <form
        className="flex flex-col gap-5"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
        autoComplete="off"
      >
        <label className="flex min-h-11 items-center gap-3 rounded-xl border border-[var(--line)] p-3">
          <input type="checkbox" checked={form.enabled} onChange={(e) => set('enabled', e.target.checked)} className="size-5 accent-[var(--color-accent)]" />
          <span className="flex-1">
            <span className="block text-sm font-medium">E-Mail-Versand aktiv</span>
            <span className="text-xs text-[var(--ink-soft)]">Ausgeschaltet bleiben E-Mails in der Warteschlange.</span>
          </span>
        </label>

        <fieldset className="grid grid-cols-1 gap-4 sm:grid-cols-6">
          <legend className="sr-only">Server</legend>
          <div className="sm:col-span-6">
            <FormField label="Vorlage (optional)" hint="Füllt Server, Port und Verschlüsselung. Zugangsdaten trägst du selbst ein.">
              <select
                className={inputCls}
                value=""
                onChange={(e) => {
                  const p = PRESETS.find((x) => x.label === e.target.value);
                  if (p) setForm((f) => ({ ...f, host: p.host, port: p.port, security: p.security }));
                }}
              >
                <option value="">Anbieter wählen …</option>
                {PRESETS.map((p) => (
                  <option key={p.label}>{p.label}</option>
                ))}
              </select>
            </FormField>
          </div>
          <div className="sm:col-span-4">
            <FormField label="SMTP-Server" htmlFor="smtp-host">
              <input id="smtp-host" className={inputCls} value={form.host} onChange={(e) => set('host', e.target.value)} placeholder="smtp.beispiel.de" autoCapitalize="off" spellCheck={false} />
            </FormField>
          </div>
          <div className="sm:col-span-2">
            <FormField label="Port" htmlFor="smtp-port">
              <input id="smtp-port" className={inputCls} type="number" inputMode="numeric" min={1} max={65535} value={form.port} onChange={(e) => set('port', Number(e.target.value))} />
            </FormField>
          </div>
          <div className="sm:col-span-6">
            <FormField label="Verschlüsselung" htmlFor="smtp-sec">
              <select
                id="smtp-sec"
                className={inputCls}
                value={form.security}
                onChange={(e) => {
                  const sec = e.target.value as SmtpSecurity;
                  setForm((f) => ({ ...f, security: sec, port: Object.values(DEFAULT_PORT).includes(f.port) ? DEFAULT_PORT[sec] : f.port }));
                }}
              >
                {(Object.keys(SECURITY_LABEL) as SmtpSecurity[]).map((k) => (
                  <option key={k} value={k}>
                    {SECURITY_LABEL[k]}
                  </option>
                ))}
              </select>
            </FormField>
          </div>
          <div className="sm:col-span-3">
            <FormField label="Benutzername" htmlFor="smtp-user" hint="Meist die vollständige E-Mail-Adresse.">
              <input id="smtp-user" className={inputCls} value={form.username} onChange={(e) => set('username', e.target.value)} autoComplete="off" autoCapitalize="off" spellCheck={false} />
            </FormField>
          </div>
          <div className="sm:col-span-3">
            <FormField
              label="Passwort"
              htmlFor="smtp-pass"
              hint={
                password === null ? (
                  <>
                    Wird beim Speichern entfernt.{' '}
                    <button type="button" className="underline" onClick={() => setPassword(undefined)}>
                      Rückgängig
                    </button>
                  </>
                ) : s.hasPassword && password === undefined ? (
                  <>
                    Ein Passwort ist gespeichert (verschlüsselt). Leer lassen, um es zu behalten.{' '}
                    <button type="button" className="underline" onClick={() => setPassword(null)}>
                      Entfernen
                    </button>
                  </>
                ) : (
                  'Wird verschlüsselt gespeichert und nie wieder angezeigt.'
                )
              }
            >
              <PasswordInput
                id="smtp-pass"
                className={inputCls}
                value={typeof password === 'string' ? password : ''}
                placeholder={s.hasPassword && password === undefined ? '•••••••• (gespeichert)' : ''}
                onChange={(e) => setPassword(e.target.value === '' ? undefined : e.target.value)}
                autoComplete="new-password"
                disabled={password === null}
              />
            </FormField>
          </div>
          <div className="sm:col-span-3">
            <FormField label="Absender-E-Mail" htmlFor="smtp-from" hint="Muss der Server versenden dürfen (oft = Benutzername).">
              <input id="smtp-from" className={inputCls} type="email" value={form.fromEmail} onChange={(e) => set('fromEmail', e.target.value)} placeholder="kanban@beispiel.de" />
            </FormField>
          </div>
          <div className="sm:col-span-3">
            <FormField label="Absender-Name" htmlFor="smtp-name">
              <input id="smtp-name" className={inputCls} value={form.fromName} onChange={(e) => set('fromName', e.target.value)} placeholder={settings.general.instanceName} />
            </FormField>
          </div>
          <div className="sm:col-span-6">
            <FormField label="Antwort an (optional)" htmlFor="smtp-reply" hint="Wohin Antworten auf KaBa-E-Mails gehen, z. B. eure Geschäftsstelle.">
              <input id="smtp-reply" className={inputCls} type="email" value={form.replyTo} onChange={(e) => set('replyTo', e.target.value)} />
            </FormField>
          </div>
        </fieldset>

        <label className="flex min-h-11 items-start gap-3">
          <input
            type="checkbox"
            checked={form.allowSelfSigned}
            onChange={(e) => set('allowSelfSigned', e.target.checked)}
            className="mt-0.5 size-5 accent-[var(--color-accent)]"
          />
          <span>
            <span className="block text-sm font-medium">Selbstsignierte Zertifikate erlauben</span>
            <span className="text-xs text-[var(--ink-soft)]">Nur für eigene Mailserver im internen Netz. Schwächt die Prüfung der Verbindung.</span>
          </span>
        </label>

        <div className="flex flex-col gap-3 rounded-xl border border-[var(--line)] p-3 sm:flex-row sm:items-end">
          <div className="flex-1">
            <FormField label="Test-E-Mail senden an" htmlFor="smtp-test">
              <input id="smtp-test" className={inputCls} type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} />
            </FormField>
          </div>
          <Button type="button" onClick={() => void test()} disabled={busy !== null || !form.host || !testTo}>
            {busy === 'test' ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Testen
          </Button>
        </div>
        <p className="-mt-2 text-xs text-[var(--ink-soft)]">Der Test nutzt die Werte im Formular, auch wenn sie noch nicht gespeichert sind.</p>
        {result && (
          <p
            role={result.ok ? 'status' : 'alert'}
            className={cx(
              'flex gap-2 rounded-lg p-3 text-sm',
              result.ok ? 'bg-emerald-50 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-100' : 'bg-rose-50 text-rose-900 dark:bg-rose-950 dark:text-rose-100',
            )}
          >
            {result.ok ? <CheckCircle2 size={18} className="shrink-0" /> : <AlertTriangle size={18} className="shrink-0" />}
            {result.message}
          </p>
        )}

        <div className="flex justify-end">
          <Button type="submit" variant="primary" disabled={busy !== null}>
            {busy === 'save' && <Loader2 size={16} className="animate-spin" />} Speichern
          </Button>
        </div>
      </form>

      <MailLog />
    </div>
  );
}

function MailLog() {
  const { data, refetch, isFetching } = useQuery({
    queryKey: ['mail-log'],
    queryFn: () => api.get<MailLogEntry[]>('/api/admin/mail-log'),
    refetchInterval: 15_000,
  });
  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center">
        <h2 className="flex-1 text-base font-semibold">Zuletzt verschickt</h2>
        <IconButton label="Aktualisieren" onClick={() => void refetch()}>
          <RotateCw size={16} className={isFetching ? 'animate-spin' : ''} />
        </IconButton>
      </div>
      {!data?.length && <p className="text-sm text-[var(--ink-soft)]">Noch keine E-Mails.</p>}
      <ul className="flex flex-col divide-y divide-[var(--line)] rounded-xl border border-[var(--line)]">
        {data?.map((m) => (
          <li key={m.id} className="flex flex-col gap-0.5 px-3 py-2.5 text-sm">
            <div className="flex items-center gap-2">
              <span
                className={cx(
                  'rounded px-1.5 py-px text-[11px] font-semibold',
                  m.status === 'sent' && 'bg-emerald-600 text-white',
                  m.status === 'pending' && 'bg-amber-400 text-amber-950',
                  m.status === 'failed' && 'bg-rose-600 text-white',
                  m.status === 'cancelled' && 'bg-black/10 text-[var(--ink)] dark:bg-white/15',
                )}
              >
                {m.status === 'sent' ? 'Gesendet' : m.status === 'pending' ? 'Wartet' : m.status === 'cancelled' ? 'Verworfen' : 'Fehlgeschlagen'}
              </span>
              <span className="min-w-0 flex-1 truncate font-medium">{m.subject}</span>
            </div>
            <span className="text-xs text-[var(--ink-soft)]">
              an {m.to} · {new Date(m.createdAt).toLocaleString('de-DE')}
              {m.attempts > 1 && ` · ${m.attempts} Versuche`}
            </span>
            {m.lastError && m.status === 'cancelled' && <span className="text-xs text-[var(--ink-soft)]">{m.lastError}</span>}
            {m.lastError && m.status !== 'sent' && m.status !== 'cancelled' && (
              <span className="text-xs text-rose-700 dark:text-rose-300">
                {m.lastError}{' '}
                <button
                  className="font-semibold underline"
                  onClick={async () => {
                    await api.post(`/api/admin/mail-log/${m.id}/retry`);
                    void refetch();
                  }}
                >
                  Erneut versuchen
                </button>
              </span>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

/* ---------- Allgemein ---------- */

function GeneralSettingsForm({ settings }: { settings: AdminSettings }) {
  const qc = useQueryClient();
  const [name, setName] = useState(settings.general.instanceName);
  const [signup, setSignup] = useState(settings.general.signup);
  const [allowPrivate, setAllowPrivate] = useState(settings.general.webhooksAllowPrivate);
  return (
    <form
      className="flex flex-col gap-5"
      onSubmit={async (e) => {
        e.preventDefault();
        try {
          const res = await api.put<AdminSettings>('/api/admin/settings/general', { instanceName: name.trim(), signup, webhooksAllowPrivate: allowPrivate });
          qc.setQueryData(['admin-settings'], res);
          void qc.invalidateQueries({ queryKey: ['config'] });
          toast.info('Gespeichert.');
        } catch (err) {
          toast.error((err as Error).message);
        }
      }}
    >
      <FormField label="Name der Instanz" htmlFor="inst-name" hint="Erscheint auf der Anmeldeseite und in E-Mails, z. B. „natec Kanban“.">
        <input id="inst-name" className={inputCls} value={name} onChange={(e) => setName(e.target.value)} required maxLength={60} />
      </FormField>
      <fieldset className="flex flex-col gap-2">
        <legend className="mb-1 text-sm font-medium">Registrierung</legend>
        {(
          [
            ['invite', 'Nur mit Einladung', 'Neue Personen können sich nur registrieren, wenn sie zu einem Board eingeladen wurden.'],
            ['open', 'Offen', 'Jede Person, die die Adresse kennt, kann ein Konto anlegen.'],
          ] as const
        ).map(([v, label, hint]) => (
          <label key={v} className={cx('flex items-start gap-3 rounded-xl border p-3', signup === v ? 'border-accent bg-accent-soft/50' : 'border-[var(--line)]')}>
            <input type="radio" name="signup" checked={signup === v} onChange={() => setSignup(v)} className="mt-0.5 size-5 accent-[var(--color-accent)]" />
            <span>
              <span className="block text-sm font-medium">{label}</span>
              <span className="text-xs text-[var(--ink-soft)]">{hint}</span>
            </span>
          </label>
        ))}
      </fieldset>
      <fieldset className="flex flex-col gap-2">
        <legend className="mb-1 text-sm font-medium">Webhooks</legend>
        <label className="flex items-start gap-3 rounded-xl border border-[var(--line)] p-3">
          <input type="checkbox" checked={allowPrivate} onChange={(e) => setAllowPrivate(e.target.checked)} className="mt-0.5 size-5 accent-[var(--color-accent)]" />
          <span>
            <span className="block text-sm font-medium">Ziele im internen Netz erlauben</span>
            <span className="text-xs text-[var(--ink-soft)]">
              Nötig, wenn Webhooks z. B. ein n8n im selben Docker-Netz oder unter 192.168.… aufrufen sollen. Nur einschalten, wenn du allen
              Board-Besitzern vertraust – sonst könnten sie damit interne Dienste ansprechen.
            </span>
          </span>
        </label>
      </fieldset>
      <FormField label="Öffentliche Adresse" htmlFor="public-url" hint="Wird über die Variable KABA_URL im Stack festgelegt.">
        <input id="public-url" className={inputCls} value={settings.publicUrl} readOnly disabled />
      </FormField>
      <BackupInfo status={settings.backup} />
      <ScannerInfo initial={settings.scanner} />
      <OfficeInfo status={settings.office} />
      <div className="flex justify-end">
        <Button type="submit" variant="primary">
          Speichern
        </Button>
      </div>
    </form>
  );
}

function formatBytes(n?: number) {
  if (!n) return '';
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 / 1024).toFixed(1).replace('.', ',')} MB`;
  return `${(n / 1024 ** 3).toFixed(2).replace('.', ',')} GB`;
}

function BackupInfo({ status }: { status: AdminSettings['backup'] }) {
  let tone = 'border-[var(--line)]';
  let text: React.ReactNode;
  if (!status) {
    text = (
      <>
        Kein Backup-Container eingebunden. Die Stack-Vorlage enthält den Dienst <code>backup</code> – siehe
        Betriebsdoku.
      </>
    );
  } else if (!status.time) {
    text = 'Backup-Container läuft, die erste Sicherung steht noch aus.';
  } else {
    const when = new Date(status.time);
    const ageH = (Date.now() - when.getTime()) / 3_600_000;
    const stale = ageH > 26;
    tone = !status.ok || stale ? 'border-amber-400 bg-amber-50 dark:bg-amber-950/40' : 'border-emerald-300 bg-emerald-50 dark:bg-emerald-950/40';
    text = (
      <>
        <span className="block font-medium">
          {status.ok ? 'Letzte Sicherung' : 'Letzte Sicherung fehlgeschlagen'}:{' '}
          {when.toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' })}
          {stale && status.ok && ' – älter als einen Tag'}
        </span>
        {status.error && <span className="block">{status.error}</span>}
        {status.ok && (
          <span className="block text-xs text-[var(--ink-soft)]">
            Datenbank {formatBytes(status.databaseBytes)} · Dateien {formatBytes(status.filesBytes)}
            {status.schedule && ` · täglich ${status.schedule} Uhr`}
            {status.keepDays && ` · ${status.keepDays} Tage aufbewahrt`}
          </span>
        )}
      </>
    );
  }
  return (
    <div className="flex flex-col gap-1">
      <span className="text-sm font-medium">Sicherung</span>
      <p className={cx('rounded-xl border p-3 text-sm', tone)}>{text}</p>
    </div>
  );
}

/** 0.78: Virenprüfung (clamd) – eigener oder gemeinsamer Scanner je Host, Adresse über KABA_CLAMAV */
function ScannerInfo({ initial }: { initial: AdminSettings['scanner'] }) {
  const [status, setStatus] = useState(initial);
  const [busy, setBusy] = useState(false);
  useEffect(() => setStatus(initial), [initial]);
  const recheck = async () => {
    setBusy(true);
    try {
      setStatus(await api.get<AdminSettings['scanner']>('/api/admin/scanner'));
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const tone =
    status.state === 'ok'
      ? 'border-emerald-300 bg-emerald-50 dark:bg-emerald-950/40'
      : status.state === 'down'
        ? 'border-amber-400 bg-amber-50 dark:bg-amber-950/40'
        : 'border-[var(--line)]';
  return (
    <div className="flex flex-col gap-1">
      <span className="text-sm font-medium">Virenprüfung</span>
      <div className={cx('flex flex-wrap items-start gap-2 rounded-xl border p-3 text-sm', tone)} role="status" aria-live="polite">
        <p className="min-w-0 flex-1">
          {status.state === 'off' ? (
            <>
              Nicht eingerichtet. Ohne Scanner lassen sich öffentliche Formulare mit Dateifragen nicht veröffentlichen. Einrichten über die Variable{' '}
              <code>KABA_CLAMAV</code> – eigener Scanner oder ein gemeinsamer für mehrere Installationen auf dem Host, siehe Betriebsdoku.
            </>
          ) : status.state === 'ok' ? (
            <>
              <span className="block font-medium">Erreichbar unter {status.address}</span>
              {(status.version || status.signatures) && (
                <span className="block text-xs text-[var(--ink-soft)]">
                  {status.version}
                  {status.signatures && ` · Signaturen ${status.signatures}`}
                </span>
              )}
            </>
          ) : (
            <>
              <span className="block font-medium">Nicht erreichbar: {status.address}</span>
              <span className="block">{status.error}</span>
              <span className="block text-xs text-[var(--ink-soft)]">Öffentliche Uploads werden abgelehnt, bis der Scanner wieder antwortet. Der erste Start dauert einige Minuten.</span>
            </>
          )}
        </p>
        {status.state !== 'off' && (
          <Button type="button" size="sm" disabled={busy} onClick={() => void recheck()}>
            Erneut prüfen
          </Button>
        )}
      </div>
    </div>
  );
}

/** 0.91: Office-Vorschau – LibreOffice ist im Image enthalten; abschaltbar über KABA_OFFICE=aus */
function OfficeInfo({ status }: { status: AdminSettings['office'] | undefined }) {
  if (!status) return null;
  return (
    <div className="flex flex-col gap-1">
      <span className="text-sm font-medium">Vorschau für Office-Dokumente</span>
      <p
        className={cx('rounded-xl border p-3 text-sm', status.available ? 'border-emerald-300 bg-emerald-50 dark:bg-emerald-950/40' : 'border-[var(--line)]')}
        role="status"
      >
        {status.available ? (
          <>
            <span className="block font-medium">Eingerichtet ({status.version})</span>
            <span className="block text-xs text-[var(--ink-soft)]">Word, Excel, PowerPoint und OpenDocument werden beim ersten Öffnen in ein PDF umgewandelt und zwischengespeichert.</span>
          </>
        ) : (
          <>Nicht verfügbar. Das offizielle Image bringt LibreOffice mit; abgeschaltet ist die Vorschau mit <code>KABA_OFFICE=aus</code>. Herunterladen geht weiterhin.</>
        )}
      </p>
    </div>
  );
}

/* ---------- Personen ---------- */

function UsersList() {
  const { data: me } = useMe();
  const { data, refetch } = useQuery({ queryKey: ['admin-users'], queryFn: () => api.get<AdminUser[]>('/api/admin/users') });
  return (
    <section className="flex flex-col gap-2">
      <p className="text-sm text-[var(--ink-soft)]">Admins verwalten E-Mail, Registrierung und Personen. Board-Rechte vergeben die Board-Besitzer.</p>
      <ul className="flex flex-col divide-y divide-[var(--line)] rounded-xl border border-[var(--line)]">
        {data?.map((u) => (
          <li key={u.id} className="flex items-center gap-3 px-3 py-2.5">
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">
                {u.name}
                {u.id === me?.id && ' (du)'}
              </p>
              <p className="truncate text-xs text-[var(--ink-soft)]">
                {u.email} · {u.boardCount} {u.boardCount === 1 ? 'Board' : 'Boards'} · seit {new Date(u.createdAt).toLocaleDateString('de-DE')}
              </p>
            </div>
            <label className="flex min-h-10 items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={u.isAdmin}
                onChange={async (e) => {
                  try {
                    await api.patch(`/api/admin/users/${u.id}`, { isAdmin: e.target.checked });
                  } catch (err) {
                    toast.error((err as Error).message);
                  }
                  void refetch();
                }}
                className="size-5 accent-[var(--color-accent)]"
              />
              Admin
            </label>
          </li>
        ))}
      </ul>
    </section>
  );
}

/* ---------- Teams: Bestand übernehmen ---------- */

interface MigrationPlan {
  teams: { id: string; name: string; memberCount: number }[];
  boards: {
    id: string;
    title: string;
    memberCount: number;
    owners: { id: string; name: string; email: string }[];
  }[];
}

/**
 * Bestehende Boards sind keinem Team zugeordnet (Bericht G01). Für eine Ein-Team-Installation kann
 * die Verwaltung sie hier ausdrücklich in ein Team übernehmen: Besitzer werden Teammitglieder, alle
 * anderen bleiben externe Gäste; Rechte ändern sich nicht (der Server vergleicht vorher und nachher).
 */
function TeamsMigration() {
  const { data, refetch } = useQuery({
    queryKey: ['admin-team-plan'],
    queryFn: () => api.get<MigrationPlan>('/api/admin/teams/plan'),
  });
  const [teamId, setTeamId] = useState('');
  const [chosen, setChosen] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  if (!data) return <Loader2 className="animate-spin text-[var(--ink-soft)]" />;
  const team = data.teams.find((t) => t.id === teamId);
  const owners = new Map<string, string>();
  for (const b of data.boards.filter((b) => chosen.includes(b.id))) for (const o of b.owners) owners.set(o.id, o.name);
  return (
    <section className="flex flex-col gap-4">
      <p className="text-sm text-[var(--ink-soft)]">
        Seit 0.23 gehören Boards zu Teams. Bestehende Boards bleiben zunächst <b>ohne Team</b> und funktionieren unverändert. Nichts wird automatisch einem Team
        zugeschlagen. Teams legt jede Person mit Konto unter „Teams“ an; Besitzer können eigene Boards selbst übernehmen.
      </p>
      <p className="text-sm text-[var(--ink-soft)]">
        Für eine Installation mit nur einem Team kannst du hier mehrere Boards auf einmal übernehmen. Die Besitzer der gewählten Boards werden dabei Mitglieder
        des Teams, alle anderen bleiben externe Gäste. Freigaben ändern sich nicht.
      </p>
      {data.teams.length === 0 ? (
        <p className="rounded-xl border border-dashed border-[var(--line)] p-4 text-sm">
          Es gibt noch kein Team. Lege zuerst unter{' '}
          <a href="/teams" onClick={(e) => (e.preventDefault(), navigate('/teams'))} className="text-accent-fg underline">
            Teams
          </a>{' '}
          eines an.
        </p>
      ) : (
        <FormField label="Zielteam" htmlFor="mig-team">
          <select
            id="mig-team"
            className="min-h-11 rounded-lg border border-[var(--line)] bg-[var(--panel)] px-3 text-sm"
            value={teamId}
            onChange={(e) => setTeamId(e.target.value)}
          >
            <option value="">Bitte wählen …</option>
            {data.teams.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.memberCount} Mitglieder)
              </option>
            ))}
          </select>
        </FormField>
      )}
      <div className="flex items-center gap-2">
        <h2 className="flex-1 text-base font-semibold">Boards ohne Team ({data.boards.length})</h2>
        {data.boards.length > 0 && (
          <Button size="sm" variant="ghost" onClick={() => setChosen(chosen.length === data.boards.length ? [] : data.boards.map((b) => b.id))}>
            {chosen.length === data.boards.length ? 'Keine' : 'Alle'} auswählen
          </Button>
        )}
      </div>
      <ul className="flex max-h-96 flex-col divide-y divide-[var(--line)] overflow-y-auto rounded-xl border border-[var(--line)]">
        {data.boards.map((b) => (
          <li key={b.id}>
            <label className="flex min-h-12 cursor-pointer items-center gap-3 px-3 py-2 text-sm">
              <input
                type="checkbox"
                className="size-5 accent-[var(--color-accent)]"
                checked={chosen.includes(b.id)}
                onChange={(e) => setChosen((l) => (e.target.checked ? [...l, b.id] : l.filter((x) => x !== b.id)))}
              />
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium">{b.title}</span>
                <span className="block truncate text-xs text-[var(--ink-soft)]">
                  Besitzer: {b.owners.map((o) => o.name).join(', ') || '–'} · {b.memberCount} mit Zugriff
                </span>
              </span>
            </label>
          </li>
        ))}
        {data.boards.length === 0 && <li className="p-3 text-sm text-[var(--ink-soft)]">Alle Boards gehören zu einem Team.</li>}
      </ul>
      {team && chosen.length > 0 && (
        <p
          className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200"
          role="status"
        >
          {chosen.length === 1 ? '1 Board' : `${chosen.length} Boards`} kommen ins Team „{team.name}“. Neu im Team (als Mitglied):{' '}
          {[...owners.values()].join(', ') || 'niemand'}. Freigaben bleiben unverändert.
        </p>
      )}
      <Button
        variant="primary"
        className="self-start"
        disabled={!team || !chosen.length || busy}
        onClick={async () => {
          if (!team || !window.confirm(`${chosen.length === 1 ? '1 Board' : `${chosen.length} Boards`} ins Team „${team.name}“ übernehmen?`)) return;
          setBusy(true);
          try {
            const r = await api.post<{ boards: number; membersAdded: number }>(`/api/admin/teams/${team.id}/adopt`, { boardIds: chosen });
            toast.info(`${r.boards} Board(s) übernommen, ${r.membersAdded} Person(en) neu im Team.`);
            setChosen([]);
          } catch (err) {
            toast.error((err as Error).message);
          } finally {
            setBusy(false);
            void refetch();
          }
        }}
      >
        Ins Team übernehmen
      </Button>
    </section>
  );
}
